import React, { useState, useCallback } from "react";
import ProgressBar from "./components/ProgressBar";
import URLInputStep from "./steps/URLInputStep";
import IcpGoalStep from "./steps/IcpGoalStep";
import TopicSwipeStep from "./steps/TopicSwipeStep";
import SectionReviewStep from "./steps/SectionReviewStep";
import NewsletterNameStep from "./steps/NewsletterNameStep";
import TemplateSelectionStep from "./steps/TemplateSelectionStep";
import AuthorStep from "./steps/AuthorStep";
import ImageStyleStep, { ImageStyle } from "./steps/ImageStyleStep";
import BuildingStep from "./steps/BuildingStep";
import ResultsStep from "./steps/ResultsStep";
import instantNewsletterService, {
  Topic,
  Section,
} from "../../services/instantNewsletterService";

type Step = "url" | "settings" | "topics" | "sections" | "name" | "author" | "template" | "images" | "building" | "results";

const STEP_INDEX: Record<Step, number> = {
  url: 0,
  settings: 1,
  topics: 2,
  sections: 3,
  name: 4,
  author: 5,
  template: 6,
  images: 7,
  building: 8,
  results: 9,
};

const STEP_LABELS = ["Website", "Settings", "Topics", "Sections", "Name", "Author", "Template", "Images", "Building", "Done"];

const STEP_ORDER: Step[] = ["url", "settings", "topics", "sections", "name", "author", "template", "images", "building", "results"];

const InstantNewsletter: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<Step>("url");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleBack = () => {
    const currentIdx = STEP_ORDER.indexOf(currentStep);
    if (currentIdx > 0) {
      setError(null);
      setCurrentStep(STEP_ORDER[currentIdx - 1]);
    }
  };

  const canGoBack = !isLoading && STEP_ORDER.indexOf(currentStep) > 0
    && currentStep !== "building" && currentStep !== "results";

  // Data
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [icpGuess, setIcpGuess] = useState("");
  const [newsletterGoal, setNewsletterGoal] = useState("");
  const [voiceAndTone, setVoiceAndTone] = useState("");
  const [topics, setTopics] = useState<Topic[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [alternativeSections, setAlternativeSections] = useState<Section[]>([]);
  const [contentWorkflow, setContentWorkflow] = useState("");
  const [sessionToken, setSessionToken] = useState("");
  const [newsletterName, setNewsletterName] = useState("");
  const [recommendedTemplateId, setRecommendedTemplateId] = useState<number | null>(null);
  const [recommendedTemplateName, setRecommendedTemplateName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [authorName, setAuthorName] = useState<string | null>(null);
  const [authorImageUrl, setAuthorImageUrl] = useState<string | null>(null);
  const [imageStyle, setImageStyle] = useState<ImageStyle>("mixed");
  const [jobId, setJobId] = useState("");
  const [result, setResult] = useState<{ magicLink: string; editionId: number } | null>(null);

  // Step 1: URL submit
  const handleUrlSubmit = async (url: string) => {
    setWebsiteUrl(url);
    setIsLoading(true);
    setError(null);
    try {
      const response = await instantNewsletterService.getTopics(url);
      setBusinessName(response.business_name);
      setIndustry(response.industry);
      setIcpGuess(response.icp_guess);
      setNewsletterGoal(response.newsletter_goal || "");
      setVoiceAndTone(response.voice_and_tone || "");
      setTopics(response.topics);
      setCurrentStep("settings");
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || "Failed to analyze website.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Settings confirm
  const handleSettingsConfirm = (icp: string, goal: string, voice: string) => {
    setIcpGuess(icp);
    setNewsletterGoal(goal);
    setVoiceAndTone(voice);
    setCurrentStep("topics");
  };

  // Step 3: Topic swipe complete
  const handleTopicsComplete = async (keptIndices: number[]) => {
    if (keptIndices.length !== 3) {
      setError("Please select exactly 3 topics to proceed.");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await instantNewsletterService.getSections(websiteUrl, keptIndices);
      setSections(response.sections);
      setAlternativeSections(response.alternative_sections || []);
      setContentWorkflow(response.content_workflow);
      setCurrentStep("sections");
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || "Failed to generate sections.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 4: Section confirm → generates strategy, gets suggested name
  const handleSectionsConfirm = async (confirmedSections: Section[]) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await instantNewsletterService.confirmSections(websiteUrl, confirmedSections);
      setSessionToken(response.session_token);
      setNewsletterName(response.strategy_summary.newsletter_name);
      setRecommendedTemplateId(response.strategy_summary.template_id);
      setRecommendedTemplateName(response.strategy_summary.template_name);
      setCurrentStep("name");
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || "Failed to generate strategy.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 5: Newsletter name confirm
  const handleNameConfirm = (name: string) => {
    setNewsletterName(name);
    setCurrentStep("author");
  };

  // Step 5b: Author personalization (optional)
  const handleAuthorConfirm = (name: string | null, imageUrl: string | null) => {
    setAuthorName(name);
    setAuthorImageUrl(imageUrl);
    setCurrentStep("template");
  };

  // Step 6: Template selection
  const handleTemplateSelect = (templateId: number) => {
    setSelectedTemplateId(templateId);
    setCurrentStep("images");
  };

  // Step 7: Image style → start build immediately
  const handleImageStyleSelect = async (style: ImageStyle) => {
    setImageStyle(style);
    setIsLoading(true);
    setError(null);
    try {
      const response = await instantNewsletterService.startBuild(
        sessionToken,
        selectedTemplateId || undefined,
        style,
        newsletterName,
        authorName || undefined,
        authorImageUrl || undefined,
      );
      setJobId(response.job_id);
      setCurrentStep("building");
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || "Failed to start build.");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 8: Build complete
  const handleBuildComplete = useCallback((buildResult: { magicLink: string; editionId: number }) => {
    setResult(buildResult);
    setCurrentStep("results");
  }, []);

  const handleBuildError = useCallback((errorMsg: string) => {
    setError(errorMsg);
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        paddingTop: "3rem",
        minHeight: "80vh",
        width: "100%",
      }}
    >
      <ProgressBar
        currentStep={STEP_INDEX[currentStep]}
        totalSteps={10}
        labels={STEP_LABELS}
      />

      {error && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#dc2626",
            padding: "12px 16px",
            borderRadius: "8px",
            marginBottom: "1rem",
            maxWidth: "500px",
            width: "100%",
            fontSize: "0.9rem",
          }}
        >
          {error}
          <button
            onClick={() => setError(null)}
            style={{ float: "right", background: "none", border: "none", cursor: "pointer", color: "#dc2626" }}
          >
            x
          </button>
        </div>
      )}

      {canGoBack && (
        <button
          onClick={handleBack}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#666",
            fontSize: "0.85rem",
            marginBottom: "1rem",
            padding: "4px 0",
          }}
        >
          ← Back
        </button>
      )}

      {currentStep === "url" && (
        <URLInputStep onSubmit={handleUrlSubmit} isLoading={isLoading} />
      )}

      {currentStep === "settings" && (
        <IcpGoalStep
          businessName={businessName}
          industry={industry}
          icpGuess={icpGuess}
          newsletterGoal={newsletterGoal}
          voiceAndTone={voiceAndTone}
          onConfirm={handleSettingsConfirm}
          isLoading={isLoading}
        />
      )}

      {currentStep === "topics" && (
        <TopicSwipeStep
          topics={topics}
          onComplete={handleTopicsComplete}
          isLoading={isLoading}
        />
      )}

      {currentStep === "sections" && (
        <SectionReviewStep
          sections={sections}
          alternativeSections={alternativeSections}
          contentWorkflow={contentWorkflow}
          onConfirm={handleSectionsConfirm}
          isLoading={isLoading}
        />
      )}

      {currentStep === "name" && (
        <NewsletterNameStep
          suggestedName={newsletterName}
          onConfirm={handleNameConfirm}
          isLoading={isLoading}
        />
      )}

      {currentStep === "author" && (
        <AuthorStep
          onConfirm={handleAuthorConfirm}
          isLoading={isLoading}
        />
      )}

      {currentStep === "template" && (
        <TemplateSelectionStep
          recommendedTemplateId={recommendedTemplateId}
          recommendedTemplateName={recommendedTemplateName}
          onSelect={handleTemplateSelect}
          isLoading={isLoading}
        />
      )}

      {currentStep === "images" && (
        <ImageStyleStep
          onSelect={handleImageStyleSelect}
          isLoading={isLoading}
        />
      )}

      {currentStep === "building" && (
        <BuildingStep
          jobId={jobId}
          onComplete={handleBuildComplete}
          onError={handleBuildError}
        />
      )}

      {currentStep === "results" && result && (
        <ResultsStep
          magicLink={result.magicLink}
          editionId={result.editionId}
          newsletterName={newsletterName}
          sessionToken={sessionToken}
        />
      )}
    </div>
  );
};

export default InstantNewsletter;
